import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/hotel_logo.png';
import { HOTEL_NAME } from "../Variables.js";

Modal.setAppElement('#root');

// Date formatters
const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Natural sort for room numbers (101 < 102 < 201)
const naturalSort = (a, b) => {
  const numA = String(a.roomNumber ?? '').trim();
  const numB = String(b.roomNumber ?? '').trim();
  return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: 'base' });
};

const Rooms = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [bookings, setBookings] = useState([]); // For real-time occupancy
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newRoom, setNewRoom] = useState({
    roomNumber: '',
    floor: '',
    roomTypeId: '',
    features: '',
    status: 'Available',
    active: true
  });
  const [editingRoom, setEditingRoom] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');

  const roomsCollectionRef = collection(db, "rooms");
  const roomTypesCollectionRef = collection(db, "roomTypes");
  const bookingsCollectionRef = collection(db, "bookings");

  useEffect(() => {
    if (user && userData) {
      const fetchAllData = async () => {
        setIsFetching(true);
        try {
          // Fetch room types and rooms concurrently
          const [roomTypesSnapshot, roomsSnapshot] = await Promise.all([
            getDocs(roomTypesCollectionRef),
            getDocs(roomsCollectionRef),
            fetchActiveBookings() // if you need this too
          ]);

          // Process room types
          const types = roomTypesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const activeTypes = types.filter(t => t.active !== false);
          setRoomTypes(activeTypes);

          // Process rooms with room type names
          const roomList = roomsSnapshot.docs.map(doc => {
            const roomData = { id: doc.id, ...doc.data() };
            const type = activeTypes.find(rt => rt.id === roomData.roomTypeId);
            return {
              ...roomData,
              roomTypeName: type?.name || '—'
            };
          });

          setRooms(roomList);
        } catch (err) {
          console.error("Error fetching data:", err);
          setError("Failed to fetch room data");
          setShowModal(true);
        } finally {
          setIsFetching(false);
        }
      };

      fetchAllData();
    }
  }, [user, userData]); // Remove the separate fetchRoomTypes and fetchRooms calls

  useEffect(() => {
    const filtered = rooms.filter((room) =>
      room.roomNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      room.features?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      room.roomTypeName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredRooms(filtered.sort(naturalSort));
  }, [searchTerm, rooms]);

  const fetchActiveBookings = async () => {
    try {
      const now = Timestamp.now();
      const q = query(
        bookingsCollectionRef,
        where("status", "in", ["confirmed", "checked-in", "pending"]),
        where("checkIn", "<=", now),
        where("checkOut", ">", now)
      );
      const snapshot = await getDocs(q);
      const active = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        roomId: doc.data().roomId
      }));
      setBookings(active);
    } catch (err) {
      console.error("Error fetching active bookings:", err);
    }
  };

  const fetchRooms = async () => {
    if (!user?.uid) return;

    setIsFetching(true);
    try {
      const snapshot = await getDocs(roomsCollectionRef);
      let roomList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Attach room type name
      roomList = roomList.map(room => {
        const type = roomTypes.find(rt => rt.id === room.roomTypeId);
        return {
          ...room,
          roomTypeName: type?.name || '—'
        };
      });

      setRooms(roomList);
    } catch (err) {
      console.error("Error fetching rooms:", err);
      setError("Failed to fetch rooms");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const isRoomCurrentlyOccupied = (roomId) => {
    return bookings.some(booking => booking.roomId === roomId);
  };

  const getDisplayStatus = (room) => {
    if (!room.active) return "Inactive";
    if (room.status === 'Out-of-Order') return "Out of Order";
    if (isRoomCurrentlyOccupied(room.id)) return "Reserved";
    return room.status; // Available or manual status
  };

  const getStatusColor = (displayStatus) => {
    switch (displayStatus) {
      case 'Available': return 'font-gold';
      case 'Reserved': return 'font-green';
      case 'Out of Order': return 'font-orange';
      case 'Occupied': return 'font-yellow';
      case 'Inactive': return 'font-gray';
      default: return 'font-gold';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRoom({ ...newRoom, [name]: value });
  };

  const handleAddRoom = async () => {
    if (!newRoom.roomNumber.trim()) {
      setError("Room number is required");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(roomsCollectionRef, {
        ...newRoom,
        roomNumber: newRoom.roomNumber.trim(),
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Room added successfully");
      fetchRooms();
      resetForm();
      setIsAddModalOpen(false);
    } catch (err) {
      console.error(err);
      setError("Failed to add room");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateRoom = async () => {
    if (!editingRoom?.id) return;
    setUpdate(true);
    try {
      const roomDoc = doc(db, "rooms", editingRoom.id);
      await updateDoc(roomDoc, {
        ...newRoom,
        roomNumber: newRoom.roomNumber.trim()
      });
      setSuccessMessage("Room updated successfully");
      fetchRooms();
      resetForm();
      setEditingRoom(null);
      setIsEditModalOpen(false);
    } catch (err) {
      console.error(err);
      setError("Failed to update room");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetForm = () => {
    setNewRoom({
      roomNumber: '',
      floor: '',
      roomTypeId: '',
      features: '',
      status: 'Available',
      active: true
    });
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setNewRoom({
      roomNumber: room.roomNumber || '',
      floor: room.floor || '',
      roomTypeId: room.roomTypeId || '',
      features: room.features || '',
      status: room.status || 'Available',
      active: room.active ?? true
    });
    setIsEditModalOpen(true);
  };

  const confirmDeactivate = (room) => {
    setCurrentRoom(room);
    setIsDeactivateModalOpen(true);
  };

  const handleDeactivate = async () => {
    if (!currentRoom?.id) return;
    setUpdate(true);
    try {
      await updateDoc(doc(db, "rooms", currentRoom.id), { active: false });
      setSuccessMessage("Room deactivated successfully");
      fetchRooms();
    } catch (err) {
      setError("Failed to deactivate room");
    } finally {
      setUpdate(false);
      setIsDeactivateModalOpen(false);
      setShowModal(true);
    }
  };

  const generateRoomsPDF = () => {
    if (rooms.length === 0) {
      setError("No rooms to export");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Staff";

    let y = 10;
    doc.addImage(Logo, "PNG", 14, y, 15, 15);
    doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.text(HOTEL_NAME, 35, y + 5);
    doc.setFontSize(14); doc.text("Rooms Inventory Report", 35, y + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, y + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, y + 23);
    y += 28;

    const sorted = [...rooms].sort(naturalSort);
    const tableData = sorted.map((room, i) => [
      (i + 1).toString(),
      room.roomNumber,
      room.floor || '-',
      room.roomTypeName || '-',
      getDisplayStatus(room),
      room.features || '-',
      room.active ? 'Active' : 'Inactive',
      room.createdAt ? dateFormatter.format(room.createdAt.toDate()) : '-'
    ]);

    autoTable(doc, {
      startY: y,
      head: [['S/N', 'Room No.', 'Floor', 'Type', 'Current Status', 'Features', 'State', 'Created']],
      body: tableData,
      theme: 'striped',
      margin: { left: 4, right: 10 },
      styles: { fontSize: 8, textColor: 0 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 15 },
        2: { cellWidth: 15 },
        3: { cellWidth: 35 },
        4: { cellWidth: 35 },
        5: { cellWidth: 45 },
        6: { cellWidth: 15 },
        7: { cellWidth: 25 }
      }
    });

    doc.save(`rooms_inventory_${formatDateForFilename(new Date())}.pdf`);
  };

  // Bulk import functions remain the same (omitted for brevity but kept in full code)

  const handleFileChange = (e) => setFile(e.target.files[0]);
  const handleFileTypeChange = (e) => setFileType(e.target.value);

  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      let dataArray = [];
      if (fileType === 'excel') {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        dataArray = XLSX.utils.sheet_to_json(sheet);
      } else {
        const text = await file.text();
        const result = Papa.parse(text, { header: true, skipEmptyLines: true });
        dataArray = result.data;
      }

      if (dataArray.length === 0) throw new Error("No data found");

      let successCount = 0;
      let errorCount = 0;

      for (let item of dataArray) {
        try {
          if (!item.roomNumber) { errorCount++; continue; }

          await addDoc(roomsCollectionRef, {
            roomNumber: String(item.roomNumber).trim(),
            floor: item.floor || '',
            roomTypeId: item.roomTypeId || '',
            features: item.features || '',
            status: ['Available', 'Reserved', 'Out-of-Order'].includes(item.status) ? item.status : 'Available',
            active: item.active !== undefined ? Boolean(item.active) : true,
            userId: user?.uid,
            createdAt: Timestamp.now()
          });
          successCount++;
        } catch (err) {
          errorCount++;
        }
      }

      setSuccessMessage(`Imported ${successCount} rooms (${errorCount} failed)`);
      fetchRooms();
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setUpdate(false);
      setFile(null);
      setIsBulkImportModalOpen(false);
      setShowModal(true);
    }
  };

  const allowedRoles = ['Admin', 'Supreme', 'Cashier', 'Front Desk'];

  if (!userData || !allowedRoles.includes(userData.status)) {
    return <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>;
  }

  const displayContent = () => {
    if (isFetching) return <p className="loading-text">Loading rooms...</p>;

    if (filteredRooms.length === 0) {
      return searchTerm
        ? <p className="loading-text">No rooms match your search.</p>
        : <p className="loading-text">No rooms added yet. Click "Add Room" to begin.</p>;
    }

    return filteredRooms.map((room) => {
      const displayStatus = getDisplayStatus(room);
      return (
        <div key={room.id} className="class-item">
          <div className="column gap-1">
            <p>
              <strong>{room.roomNumber}</strong><br />
              <span>Floor: {room.floor || '—'}</span><br />
              <span>Type: {room.roomTypeName}</span><br />
              {room.features && <span>Features: {room.features}</span>}
              {!room.features && <span className="size5">No features listed</span>}
              <br />
              <span className={`size5 bold ${getStatusColor(displayStatus)}`}>{displayStatus}</span>
              {!room.active && <span className="size5"> (Deactivated)</span>}
            </p>
          </div>
          <div className="row align-center justify-center gap-15">
            <button className="list-btn" onClick={() => handleEdit(room)}>
              <i className="fas fa-edit"></i>
            </button>
            {room.active && (
              <button className="list-btn delete-btn" onClick={() => confirmDeactivate(room)}>
                <i className="fas fa-ban"></i>
              </button>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      <div className="top-elements row align-center space-between">
        <Link to="/home" className="back-link">
          <i className="fa-solid fa-arrow-left size3"></i>
        </Link>
        <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Rooms</h2>
        <Link to="#" className="home-link" onClick={fetchRooms}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>
      </div>

      <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed4">
        <div className="row justify-center align-center gap-15 add-buttons add-buttons1 margin-top-cashflow1">
          <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Room</button>
          <button className="list-btn" onClick={generateRoomsPDF}><i className="fas fa-print"></i></button>
          <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button>
        </div>

        <input
          type="text"
          className="input-field1"
          placeholder="Search rooms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="scroll-fixed scroll-fixed1">
          <div className="class-list class-list-d scrollable">
            {displayContent()}
          </div>
        </div>
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* Add Modal */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => setIsAddModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Room</h2>
        <form className="session-form">
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Room Number</label>
              <input type="text" name="roomNumber" className="input-field" placeholder="e.g., 101, A12" value={newRoom.roomNumber} onChange={handleInputChange} required />
            </div>
            <div>
              <label className="input-label">Floor</label>
              <input type="text" name="floor" className="input-field" placeholder="e.g., Ground, 1st, 2nd" value={newRoom.floor} onChange={handleInputChange} />
            </div>
          </div>

          <label className="input-label">Room Type</label>
          <select name="roomTypeId" className="input-field" value={newRoom.roomTypeId} onChange={handleInputChange}>
            <option value="">Select Room Type</option>
            {roomTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name} ({type.code || 'No code'})</option>
            ))}
          </select>

          <label className="input-label">Features / Notes</label>
          <input type="text" name="features" className="input-field" placeholder="e.g., Sea view, King bed, Balcony" value={newRoom.features} onChange={handleInputChange} />

          <label className="input-label">Status</label>
          <select name="status" className="input-field" value={newRoom.status} onChange={handleInputChange}>
            <option value="Available">Available</option>
            <option value="Reserved">Reserved</option>
            <option value="Out-of-Order">Out-of-Order</option>
          </select>

          <div className="row align-center gap-1">
            <button type="button" className="login-button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button type="button" className="login-button" onClick={handleAddRoom}>Add Room</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => { setIsEditModalOpen(false); resetForm(); setEditingRoom(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Room</h2>
        <form className="session-form">
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Room Number</label>
              <input type="text" name="roomNumber" className="input-field" value={newRoom.roomNumber} onChange={handleInputChange} required />
            </div>
            <div>
              <label className="input-label">Floor</label>
              <input type="text" name="floor" className="input-field" value={newRoom.floor} onChange={handleInputChange} />
            </div>
          </div>

          <label className="input-label">Room Type</label>
          <select name="roomTypeId" className="input-field" value={newRoom.roomTypeId} onChange={handleInputChange}>
            <option value="">None</option>
            {roomTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>

          <label className="input-label">Features / Notes</label>
          <input type="text" name="features" className="input-field" value={newRoom.features} onChange={handleInputChange} />

          <label className="input-label">Status</label>
          <select name="status" className="input-field" value={newRoom.status} onChange={handleInputChange}>
            <option value="Available">Available</option>
            <option value="Reserved">Reserved</option>
            <option value="Out-of-Order">Out-of-Order</option>
          </select>

          <label>
            <input type="checkbox" className='gold-tick' checked={newRoom.active} onChange={(e) => setNewRoom({ ...newRoom, active: e.target.checked })} />
            Active
          </label>

          <div className="row align-center justify-center gap-1">
            <button type="button" className="login-button" onClick={() => { setIsEditModalOpen(false); resetForm(); setEditingRoom(null); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleUpdateRoom}>Update Room</button>
          </div>
        </form>
      </Modal>

      {/* Deactivate Modal */}
      <Modal isOpen={isDeactivateModalOpen} onRequestClose={() => setIsDeactivateModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Deactivate Room</h2>
        {currentRoom && (
          <div>
            <p>Deactivate room <strong>{currentRoom.roomNumber}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" onClick={() => setIsDeactivateModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" onClick={() => handleDeactivate(currentRoom.id)}>Deactivate</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={isBulkImportModalOpen} onRequestClose={() => { setIsBulkImportModalOpen(false); setFile(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Bulk Import Rooms</h2>
        <div className="modal-form">
          <div style={{ marginBottom: '5px' }}>
            <label><input type="radio" name="fileType" value="excel" checked={fileType === 'excel'} onChange={handleFileTypeChange} /> Excel</label>&nbsp;&nbsp;
            <label><input type="radio" name="fileType" value="csv" checked={fileType === 'csv'} onChange={handleFileTypeChange} /> CSV</label>
          </div>

          <input type="file" accept={fileType === 'excel' ? '.xlsx,.xls' : '.csv'} onChange={handleFileChange} className="input-field1" />

          <div className="row align-center justify-center gap-15">
            <button className="login-button caps" onClick={() => { setIsBulkImportModalOpen(false); setFile(null); }}>Cancel</button>
            <button className="login-button caps" onClick={processBulkImport} disabled={!file || update}>Import</button>
          </div>

          <div className="import-instructions">
            <h4>Format Instructions:</h4>
            <p><strong>REQUIRED:</strong> roomNumber</p>
            <p><strong>OPTIONAL:</strong> floor, features, status (Available/Reserved/Out-of-Order), active</p>
            <p>Note: Room type is to be selected in the app.</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Rooms;